/**
 * ERC (Electrical Rule Check) Types
 *
 * This module defines types and interfaces for electrical rule checking,
 * based on KiCad's ERC implementation. ERC validates schematic designs
 * for electrical correctness before PCB layout.
 *
 * @see https://gitlab.com/kicad/code/kicad/-/tree/master/eeschema/erc
 */
/**
 * Electrical pin types used for ERC validation.
 * These map to KiCad's ELECTRICAL_PINTYPE enum.
 *
 * @see KiCad's eeschema/pin_type.h
 */
export var ElectricalPinType;
(function (ElectricalPinType) {
    /** Input pin - receives signals */
    ElectricalPinType["INPUT"] = "input";
    /** Output pin - drives signals */
    ElectricalPinType["OUTPUT"] = "output";
    /** Bidirectional pin - can both receive and drive signals */
    ElectricalPinType["BIDIRECTIONAL"] = "bidirectional";
    /** Tri-state pin - can be high, low, or high-impedance */
    ElectricalPinType["TRISTATE"] = "tristate";
    /** Passive pin - resistors, capacitors, etc. */
    ElectricalPinType["PASSIVE"] = "passive";
    /** Free/Unspecified pin - no specific electrical function */
    ElectricalPinType["UNSPECIFIED"] = "unspecified";
    /** Power input pin - receives power (VCC, GND) */
    ElectricalPinType["POWER_IN"] = "power_in";
    /** Power output pin - provides power */
    ElectricalPinType["POWER_OUT"] = "power_out";
    /** Open collector output */
    ElectricalPinType["OPEN_COLLECTOR"] = "open_collector";
    /** Open emitter output */
    ElectricalPinType["OPEN_EMITTER"] = "open_emitter";
    /** Not connected - should not be connected to anything */
    ElectricalPinType["NO_CONNECT"] = "no_connect";
})(ElectricalPinType || (ElectricalPinType = {}));
/**
 * ERC error codes based on KiCad's ERCE_T enum.
 * These define the types of electrical rule violations that can be detected.
 *
 * @see KiCad's eeschema/erc/erc_settings.h
 */
export var ErcErrorCode;
(function (ErcErrorCode) {
    /** Unspecified error */
    ErcErrorCode["UNSPECIFIED"] = "ERCE_UNSPECIFIED";
    /** Duplicate sheet names in hierarchical design */
    ErcErrorCode["DUPLICATE_SHEET_NAME"] = "ERCE_DUPLICATE_SHEET_NAME";
    /** Wire endpoint not on grid */
    ErcErrorCode["ENDPOINT_OFF_GRID"] = "ERCE_ENDPOINT_OFF_GRID";
    /** Pin not connected to anything */
    ErcErrorCode["PIN_NOT_CONNECTED"] = "ERCE_PIN_NOT_CONNECTED";
    /** Pin not driven by any output */
    ErcErrorCode["PIN_NOT_DRIVEN"] = "ERCE_PIN_NOT_DRIVEN";
    /** Power pin not driven */
    ErcErrorCode["POWERPIN_NOT_DRIVEN"] = "ERCE_POWERPIN_NOT_DRIVEN";
    /** Hierarchical label mismatch */
    ErcErrorCode["HIERARCHICAL_LABEL"] = "ERCE_HIERARCHICAL_LABEL";
    /** No-connect symbol connected to something */
    ErcErrorCode["NOCONNECT_CONNECTED"] = "ERCE_NOCONNECT_CONNECTED";
    /** No-connect symbol not connected (redundant) */
    ErcErrorCode["NOCONNECT_NOT_CONNECTED"] = "ERCE_NOCONNECT_NOT_CONNECTED";
    /** Label not connected to anything */
    ErcErrorCode["LABEL_NOT_CONNECTED"] = "ERCE_LABEL_NOT_CONNECTED";
    /** Similar labels (potential typo) */
    ErcErrorCode["SIMILAR_LABELS"] = "ERCE_SIMILAR_LABELS";
    /** Similar power net names */
    ErcErrorCode["SIMILAR_POWER"] = "ERCE_SIMILAR_POWER";
    /** Similar label and power net name */
    ErcErrorCode["SIMILAR_LABEL_AND_POWER"] = "ERCE_SIMILAR_LABEL_AND_POWER";
    /** Global label used only once */
    ErcErrorCode["SINGLE_GLOBAL_LABEL"] = "ERCE_SINGLE_GLOBAL_LABEL";
    /** Same name used for local and global label */
    ErcErrorCode["SAME_LOCAL_GLOBAL_LABEL"] = "ERCE_SAME_LOCAL_GLOBAL_LABEL";
    /** Different footprint for multi-unit symbol */
    ErcErrorCode["DIFFERENT_UNIT_FOOTPRINT"] = "ERCE_DIFFERENT_UNIT_FP";
    /** Missing power input pin connection */
    ErcErrorCode["MISSING_POWER_INPUT_PIN"] = "ERCE_MISSING_POWER_INPUT_PIN";
    /** Missing input pin connection */
    ErcErrorCode["MISSING_INPUT_PIN"] = "ERCE_MISSING_INPUT_PIN";
    /** Missing bidirectional pin connection */
    ErcErrorCode["MISSING_BIDI_PIN"] = "ERCE_MISSING_BIDI_PIN";
    /** Missing unit in multi-unit symbol */
    ErcErrorCode["MISSING_UNIT"] = "ERCE_MISSING_UNIT";
    /** Different net for same pin across units */
    ErcErrorCode["DIFFERENT_UNIT_NET"] = "ERCE_DIFFERENT_UNIT_NET";
    /** Bus alias conflict */
    ErcErrorCode["BUS_ALIAS_CONFLICT"] = "ERCE_BUS_ALIAS_CONFLICT";
    /** Multiple drivers on same net */
    ErcErrorCode["DRIVER_CONFLICT"] = "ERCE_DRIVER_CONFLICT";
    /** Bus entry conflict */
    ErcErrorCode["BUS_ENTRY_CONFLICT"] = "ERCE_BUS_ENTRY_CONFLICT";
    /** Bus to bus conflict */
    ErcErrorCode["BUS_TO_BUS_CONFLICT"] = "ERCE_BUS_TO_BUS_CONFLICT";
    /** Bus to net conflict */
    ErcErrorCode["BUS_TO_NET_CONFLICT"] = "ERCE_BUS_TO_NET_CONFLICT";
    /** Ground pin not connected to ground net */
    ErcErrorCode["GROUND_PIN_NOT_GROUND"] = "ERCE_GROUND_PIN_NOT_GROUND";
    /** Label connected to only one pin */
    ErcErrorCode["LABEL_SINGLE_PIN"] = "ERCE_LABEL_SINGLE_PIN";
    /** Unresolved text variable */
    ErcErrorCode["UNRESOLVED_VARIABLE"] = "ERCE_UNRESOLVED_VARIABLE";
    /** Undefined net class */
    ErcErrorCode["UNDEFINED_NETCLASS"] = "ERCE_UNDEFINED_NETCLASS";
    /** Simulation model issue */
    ErcErrorCode["SIMULATION_MODEL"] = "ERCE_SIMULATION_MODEL";
    /** Dangling wire */
    ErcErrorCode["WIRE_DANGLING"] = "ERCE_WIRE_DANGLING";
    /** Library symbol issues */
    ErcErrorCode["LIB_SYMBOL_ISSUES"] = "ERCE_LIB_SYMBOL_ISSUES";
    /** Library symbol mismatch */
    ErcErrorCode["LIB_SYMBOL_MISMATCH"] = "ERCE_LIB_SYMBOL_MISMATCH";
    /** Footprint link issues */
    ErcErrorCode["FOOTPRINT_LINK_ISSUES"] = "ERCE_FOOTPRINT_LINK_ISSUES";
    /** Footprint filter issues */
    ErcErrorCode["FOOTPRINT_FILTERS"] = "ERCE_FOOTPRINT_FILTERS";
    /** Unannotated symbol */
    ErcErrorCode["UNANNOTATED"] = "ERCE_UNANNOTATED";
    /** Extra units in symbol */
    ErcErrorCode["EXTRA_UNITS"] = "ERCE_EXTRA_UNITS";
    /** Different value for multi-unit symbol */
    ErcErrorCode["DIFFERENT_UNIT_VALUE"] = "ERCE_DIFFERENT_UNIT_VALUE";
    /** Duplicate reference designator */
    ErcErrorCode["DUPLICATE_REFERENCE"] = "ERCE_DUPLICATE_REFERENCE";
    /** Bus entry needed */
    ErcErrorCode["BUS_ENTRY_NEEDED"] = "ERCE_BUS_ENTRY_NEEDED";
    /** Four-way junction */
    ErcErrorCode["FOUR_WAY_JUNCTION"] = "ERCE_FOUR_WAY_JUNCTION";
    /** Label connected to multiple wires */
    ErcErrorCode["LABEL_MULTIPLE_WIRES"] = "ERCE_LABEL_MULTIPLE_WIRES";
    /** Unconnected wire endpoint */
    ErcErrorCode["UNCONNECTED_WIRE_ENDPOINT"] = "ERCE_UNCONNECTED_WIRE_ENDPOINT";
    /** Stacked pin syntax issue */
    ErcErrorCode["STACKED_PIN_SYNTAX"] = "ERCE_STACKED_PIN_SYNTAX";
    /** Duplicate pin error */
    ErcErrorCode["DUPLICATE_PIN_ERROR"] = "ERCE_DUPLICATE_PIN_ERROR";
    /** Pin-to-pin warning */
    ErcErrorCode["PIN_TO_PIN_WARNING"] = "ERCE_PIN_TO_PIN_WARNING";
    /** Pin-to-pin error */
    ErcErrorCode["PIN_TO_PIN_ERROR"] = "ERCE_PIN_TO_PIN_ERROR";
    /** Single pin on a net */
    ErcErrorCode["SINGLE_PIN_NET"] = "ERCE_SINGLE_PIN_NET";
})(ErcErrorCode || (ErcErrorCode = {}));
/**
 * Severity levels for ERC violations.
 */
export var ErcSeverity;
(function (ErcSeverity) {
    /** No issue */
    ErcSeverity["OK"] = "ok";
    /** Warning - potential issue but may be intentional */
    ErcSeverity["WARNING"] = "warning";
    /** Error - definite electrical rule violation */
    ErcSeverity["ERROR"] = "error";
})(ErcSeverity || (ErcSeverity = {}));
//# sourceMappingURL=types.js.map